import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import { TypeChecker } from '@attain-apps/type-guards'

import * as state from '../../../../../../../state'

import * as generalTab from './general-tab'
import * as queryTab from './query-tab'

@ngCore.Component({
  imports: [
    generalTab.Component,
    queryTab.Component,
    ds.layout.page.Component,
    ds.layout.page.child.header.Component,
    ds.tabs.integratedHorizontal.Component,
    ds.tabs.tab.Directive,
    ds.button.Component,
    ds.loader.Component,
  ],
  selector: 'atn-mosaic-dev-widget-builder',
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component implements ngCore.OnDestroy {
  /** @see {@link state.mosaic.dev.widgets.Collection} */
  public readonly widgetsEc = ngCore.inject(state.mosaic.dev.widgets.Collection)

  /** @see {@link state.me.Feature} */
  public readonly meFeature = ngCore.inject(state.me.Feature)

  /**
   * Breadcrumbs displayed in page header
   */
  public readonly breadcrumbsSg = ngCore.computed(
    () =>
      [
        {
          name: 'Widgets',
          url: this.meFeature.contexts.selectors
            .getUrlForLink({
              goto: {
                navigationId: ents.extra.prebuilt.AppPageAlias.Widgets,
              },
            })
            .asSignal(),
        },
        {
          name:
            this.widgetsEc.plugins.currentEntityPlugin.feature.selectors
              .getLatestEntity()
              .asSignal()?.name ?? '',
        },
      ] satisfies readonly ds.layout.page.child.header.models.Breadcrumb[],
  )

  /** Indicates whether the currently selected entity is loading*/
  public readonly isLoadingSg = ngCore.computed(
    (
      id = this.widgetsEc.plugins.currentEntityPlugin.feature.selectors
        .getId()
        .asSignal(),
    ) =>
      id
        ? this.widgetsEc.plugins.dataOperationPlugin.feature.selectors
            .isLoading(id)
            .asSignal()
        : false,
  )

  /**
   * Determines whether to automatically initiate edit mode for the current
   * entity when no entity is currently being edited.
   *
   * (This typically happens when a user refreshes the page while in edit mode)
   */
  private readonly _shouldInitiateEditSg = ngCore.computed(
    (
      id = this.widgetsEc.plugins.currentEntityPlugin.feature.selectors
        .getId()
        .asSignal(),
      editedEntity = this.widgetsEc.plugins.currentEntityPlugin.feature.selectors
        .getEditedEntity()
        .asSignal(),
    ) => !!id && !editedEntity,
  )

  constructor() {
    ngCore.effect(() => {
      const shouldInitiateEdit = this._shouldInitiateEditSg()

      if (shouldInitiateEdit) {
        this.widgetsEc.plugins.editingPlugin.feature.actions.edit
          .from('WidgetBuilder.Component.Init()')
          .dispatch({
            id: this.widgetsEc.plugins.currentEntityPlugin.feature.selectors
              .getId()
              .asSignal(),
          })
      }
    })
  }

  /** Save widget button click handler */
  public saveButtonClickHandler(): void {
    this.widgetsEc.plugins.editingPlugin.feature.actions.submit
      .from('WidgetBuilder.Component.Buttons.Save.Clicked')
      .dispatch({
        current: true,
        // Unset current entity after creation, but maintain selection when
        // editing existing widgets to achieve behavior:
        // - For existing widgets: stay on builder page after save
        // - For new widgets: return to widgets list after creation
        skipUnset: !this.widgetsEc.plugins.currentEntityPlugin.feature.selectors
          .isCreating()
          .asSignal(),
      })
  }

  /**
   * In case of redirecting to some other page from widget builder page, we want
   * to discard changes that we made
   */
  ngOnDestroy() {
    this.widgetsEc.plugins.editingPlugin.feature.actions.discard
      .from('AtnMosaicDevWidgetBuilderPage.ngOnDestroy()')
      .dispatch({ current: true })
  }
}
